/**
 * The Testing tab's **Compare** section (task 1227), replacing the compare
 * checkboxes, "Compare 2 selected" and the separate comparison page.
 *
 * Two selectors on one line, **Compare** (`a`, the baseline) and **with**
 * (`b`, the candidate), each listing the service's settled tests newest first,
 * and the report (`TestComparison`) inline below them. The pair lives in the
 * URL as `?section=compare&a=&b=`, so a comparison is linkable.
 *
 * Defaults: **with** is the newest settled test; **Compare** is the service's
 * suggested baseline for it (`GET /endpoint-test-run-groups/:id/compare-suggestion`),
 * falling back to the next-newest settled test. A row's Compare in Tests
 * arrives with `b` only, so the same rule fills in `a`. Swap is one button.
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowsRightLeftIcon, ScaleIcon } from '@heroicons/react/24/outline';
import {
  isTestRunning,
  useCompareSuggestion,
  useEndpointTestRunGroups,
  useExportTestRunComparison,
} from '@/hooks/useEndpointTestRunGroups';
import { useToast } from '@/contexts/ToastContext';
import { useEndpointTests } from '@/hooks/useEndpointTests';
import { originPhrase, targetLabel, testNumberLabel } from '@/utils/testRunResults';
import { TestComparison } from './TestComparison';
import type { EndpointTestRunGroup } from '@/types/endpointTests';

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * A test as the selectors list it: the Tests list's identity line (task 1232)
 * with the day folded in, since a selector has no day headers —
 * "#12 · Oct 20, 3:04 PM · staging · v1.3 · Suite Regression pack · 8 scenarios".
 */
export function testOptionLabel(
  group: EndpointTestRunGroup,
  scenarioNames: ReadonlyMap<string, string> = new Map(),
  numbers: ReadonlyMap<string, number | undefined> = new Map(),
): string {
  return [
    testNumberLabel(group),
    TIME_FORMAT.format(new Date(group.createdAt)),
    targetLabel(group),
    originPhrase(group, scenarioNames, numbers),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The service's tests that have finished, newest first (the list's own order). */
export function settledTests(groups: EndpointTestRunGroup[]): EndpointTestRunGroup[] {
  return groups.filter((group) => !isTestRunning(group));
}

export function TestCompareSection({ endpointId }: { endpointId: string }) {
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const a = params.get('a') ?? '';
  const b = params.get('b') ?? '';

  const { data: groupsData, isLoading } = useEndpointTestRunGroups(endpointId);
  const settled = useMemo(() => settledTests(groupsData?.data ?? []), [groupsData]);
  const { data: testsData } = useEndpointTests(endpointId);
  const scenarioNames = useMemo(
    () => new Map((testsData?.data ?? []).map((test) => [test.id, test.name])),
    [testsData],
  );
  const numbers = useMemo(
    () => new Map((groupsData?.data ?? []).map((group) => [group.id, group.number])),
    [groupsData],
  );

  // The candidate defaults to the newest settled test; the baseline to the
  // service's suggestion for it, else the next-newest.
  const candidate = b || settled[0]?.id || '';
  const suggestion = useCompareSuggestion(!a && candidate ? candidate : undefined);
  const fallbackBaseline = settled.find((group) => group.id !== candidate)?.id ?? '';
  const baseline = a || (suggestion.isFetched ? suggestion.data || fallbackBaseline : '');

  const write = (next: { a?: string; b?: string }) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set('section', 'compare');
        for (const side of ['a', 'b'] as const) {
          const value = next[side];
          if (value === undefined) continue;
          if (value) out.set(side, value);
          else out.delete(side);
        }
        return out;
      },
      { replace: true },
    );

  // Fill the defaults into the URL once they are known, so the link says what is shown.
  useEffect(() => {
    if (!candidate || !baseline) return;
    if (a === baseline && b === candidate) return;
    write({ a: baseline, b: candidate });
    // `write` is recreated each render; the pair is what decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, baseline, candidate]);

  const exportComparison = useExportTestRunComparison();
  const handleExport = async (format: 'csv' | 'json') => {
    if (!baseline || !candidate) return;
    try {
      await exportComparison.mutateAsync({ a: baseline, b: candidate, format });
    } catch (err) {
      toast.error('Export failed', err instanceof Error ? err.message : undefined);
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const selectClass =
    'min-w-0 max-w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white';
  const quietButton =
    'px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50';
  const ready = !!baseline && !!candidate && baseline !== candidate;
  // "#9 vs #12" once both sides carry a number (task 1232).
  const aNumber = numbers.get(baseline);
  const bNumber = numbers.get(candidate);
  const heading =
    ready && typeof aNumber === 'number' && typeof bNumber === 'number' ? `#${aNumber} vs #${bNumber}` : 'Compare';

  const options = settled.map((group) => (
    <option key={group.id} value={group.id}>
      {testOptionLabel(group, scenarioNames, numbers)}
    </option>
  ));

  return (
    <section className="space-y-4" data-testid="service-testing-compare">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <ScaleIcon className="h-5 w-5 mr-2 text-gray-400" />
            <span data-testid="compare-heading">{heading}</span>
          </h3>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void handleExport('csv')} disabled={!ready || exportComparison.isPending} className={quietButton}>
              Export CSV
            </button>
            <button type="button" onClick={() => void handleExport('json')} disabled={!ready || exportComparison.isPending} className={quietButton}>
              Export JSON
            </button>
            <button type="button" onClick={() => void copyLink()} disabled={!ready} className={quietButton}>
              Copy link
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
          {isLoading ? (
            <span className="text-gray-500 dark:text-gray-400">Loading tests…</span>
          ) : settled.length < 2 ? (
            <span className="text-gray-500 dark:text-gray-400" data-testid="compare-not-enough">
              Comparing needs two finished tests.
            </span>
          ) : (
            <>
              <label htmlFor="compare-pick-a">Compare</label>
              <select id="compare-pick-a" value={baseline} onChange={(e) => write({ a: e.target.value })} className={selectClass}>
                {!baseline && <option value="">Choose a test</option>}
                {options}
              </select>
              <button
                type="button"
                onClick={() => write({ a: candidate, b: baseline })}
                disabled={!ready}
                aria-label="Swap the two tests"
                title="Swap"
                data-testid="compare-swap"
                className="p-1 text-gray-500 hover:text-indigo-600 dark:text-gray-400 disabled:opacity-50"
              >
                <ArrowsRightLeftIcon className="h-4 w-4" />
              </button>
              <label htmlFor="compare-pick-b">with</label>
              <select id="compare-pick-b" value={candidate} onChange={(e) => write({ b: e.target.value })} className={selectClass}>
                {options}
              </select>
            </>
          )}
        </div>
      </div>

      {ready && <TestComparison endpointId={endpointId} a={baseline} b={candidate} />}
      {settled.length >= 2 && baseline && baseline === candidate && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Pick two different tests to compare.</p>
      )}
    </section>
  );
}

export default TestCompareSection;
